import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppLayout } from "../components/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api } from "../lib/api";
import { Check, Loader2, Save } from "lucide-react";

const TOPICS = ["Family", "Hobbies", "Memories", "Sports", "Faith", "Current Events", "Gardening", "Cooking", "Pets", "Travel"];

export default function LovedOne() {
  const [form, setForm] = useState(null);
  const [schedule, setSchedule] = useState({ frequency: "daily", days: [], preferred_time: "10:00" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [familyInput, setFamilyInput] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/loved-one");
        setForm(data.loved_one || {
          name: "", relationship: "Mother", phone: "", timezone_name: "America/New_York",
          topics: [], family_members: [], preferences: "", paused: false,
        });
        if (data.schedule) setSchedule(data.schedule);
      } finally { setLoading(false); }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTopic = (t) => set("topics", form.topics?.includes(t) ? form.topics.filter((x) => x !== t) : [...(form.topics || []), t]);
  const addFamily = () => {
    const v = familyInput.trim();
    if (!v) return;
    set("family_members", [...(form.family_members || []), v]);
    setFamilyInput("");
  };
  const removeFamily = (i) => set("family_members", form.family_members.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/loved-one", form);
      await api.put("/loved-one/schedule", schedule);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  if (loading || !form) {
    return <AppLayout><div className="grid place-items-center h-96"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></AppLayout>;
  }

  return (
    <AppLayout
      title="Loved one profile"
      subtitle="The more CherishLink knows, the warmer each call becomes."
      actions={
        <Button onClick={save} disabled={saving} className="rounded-full" data-testid="lo-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save changes
        </Button>
      }
    >
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 rounded-3xl border-border/60 soft-shadow">
          <CardContent className="p-6 md:p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input data-testid="lo-name" className="mt-2 h-11 rounded-xl" value={form.name} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div>
                <Label>Relationship</Label>
                <Select value={form.relationship} onValueChange={(v) => set("relationship", v)}>
                  <SelectTrigger data-testid="lo-relationship" className="mt-2 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Mother","Father","Grandmother","Grandfather","Aunt","Uncle","Spouse","Other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input data-testid="lo-phone" className="mt-2 h-11 rounded-xl" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
              <div>
                <Label>Time zone</Label>
                <Select value={form.timezone_name || "America/New_York"} onValueChange={(v) => set("timezone_name", v)}>
                  <SelectTrigger className="mt-2 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["America/New_York","America/Chicago","America/Denver","America/Los_Angeles","America/Phoenix","America/Anchorage","Pacific/Honolulu"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Topics they enjoy</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {TOPICS.map((t) => {
                  const active = form.topics?.includes(t);
                  return (
                    <button key={t} type="button" onClick={() => toggleTopic(t)}
                      data-testid={`lo-topic-${t.toLowerCase().replace(/ /g, "-")}`}
                      className={`px-3.5 py-1.5 rounded-full text-sm border-2 transition-all ${
                        active ? "bg-primary text-primary-foreground border-primary" : "border-border bg-white hover:border-primary/30"
                      }`}>
                      {active && <Check className="inline h-3 w-3 mr-1" />}{t}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Family members to mention</Label>
              <div className="mt-2 flex gap-2">
                <Input className="h-11 rounded-xl" placeholder="E.g. Emma (granddaughter)"
                  value={familyInput} onChange={(e) => setFamilyInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFamily(); } }}
                  data-testid="lo-family-input" />
                <Button type="button" variant="outline" onClick={addFamily} className="rounded-xl h-11" data-testid="lo-family-add">Add</Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(form.family_members || []).map((m, i) => (
                  <button key={i} onClick={() => removeFamily(i)} className="px-3 py-1 rounded-full bg-accent text-accent-foreground text-sm hover:bg-accent/70">
                    {m} ×
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Notes & preferences</Label>
              <Textarea rows={3} data-testid="lo-preferences" className="mt-2 rounded-xl"
                placeholder="Anything that helps CherishLink have warmer conversations..."
                value={form.preferences || ""} onChange={(e) => set("preferences", e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <div className="space-y-5">
          <Card className="rounded-3xl border-border/60 soft-shadow">
            <CardContent className="p-6 md:p-8 space-y-4">
              <div>
                <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Call schedule</div>
                <h3 className="mt-1 font-display text-lg font-semibold">When to check in</h3>
              </div>
              <div>
                <Label>Frequency</Label>
                <Select value={schedule.frequency} onValueChange={(v) => setSchedule((s) => ({ ...s, frequency: v }))}>
                  <SelectTrigger data-testid="lo-sched-freq" className="mt-2 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="3x_weekly">3× per week</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preferred time</Label>
                <Input type="time" className="mt-2 h-11 rounded-xl" data-testid="lo-sched-time"
                  value={schedule.preferred_time} onChange={(e) => setSchedule((s) => ({ ...s, preferred_time: e.target.value }))} />
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-border/60 soft-shadow">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">Pause calls</div>
                  <div className="text-sm text-muted-foreground">Take a break — no calls will be made.</div>
                </div>
                <Switch checked={!!form.paused} onCheckedChange={(v) => set("paused", v)} data-testid="lo-pause-toggle" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
